import React from 'react';
import { Bars3Icon } from '@heroicons/react/24/outline';

export type SectionRailMobileTriggerProps = {
  /** Primary label (e.g. Discover, Organizer, Admin). */
  title: string;
  /** Secondary context after the · (active section / page). */
  subtitle?: string | null;
  /** Optional count badge (filters, pending apps, …). */
  badgeCount?: number;
  expanded?: boolean;
  controlsId?: string;
  onClick: () => void;
  className?: string;
  /** Accessible name; defaults to title + subtitle. */
  'aria-label'?: string;
  'data-testid'?: string;
};

/**
 * Canonical mobile subsection chrome — matches Discover / build tools:
 * rounded pill, hamburger left, Title · Subtitle.
 */
export const SectionRailMobileTrigger: React.FC<SectionRailMobileTriggerProps> = ({
  title,
  subtitle,
  badgeCount = 0,
  expanded = false,
  controlsId,
  onClick,
  className = '',
  'aria-label': ariaLabel,
  'data-testid': testId,
}) => {
  const label =
    ariaLabel ||
    [title, subtitle ? String(subtitle) : null].filter(Boolean).join(' · ');

  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      aria-controls={controlsId}
      aria-label={label}
      data-testid={testId}
      className={`section-rail-mobile-trigger inline-flex items-center gap-2.5 w-full min-h-[44px] px-3.5 py-2.5 text-left text-sm font-medium text-white rounded-2xl border border-white/[0.1] bg-[#141414] hover:border-white/20 hover:bg-white/[0.06] transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 ${className}`.trim()}
    >
      <Bars3Icon className="w-5 h-5 shrink-0 text-white" aria-hidden />
      <span className="truncate font-semibold tracking-tight">{title}</span>
      {subtitle ? (
        <>
          <span className="text-gray-500 shrink-0" aria-hidden>
            ·
          </span>
          <span className="truncate text-gray-400 font-normal min-w-0">{subtitle}</span>
        </>
      ) : null}
      {badgeCount > 0 ? (
        <span className="ml-auto shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-md bg-orange-500/20 text-orange-300 text-xs font-semibold tabular-nums">
          {badgeCount}
        </span>
      ) : null}
    </button>
  );
};

export default SectionRailMobileTrigger;
